export const convertToDMS = (decimal: number, type: 'lat' | 'lon') => {
  const degrees = Math.floor(Math.abs(decimal));
  const minutes = Math.floor((Math.abs(decimal) - degrees) * 60);
  const seconds = ((Math.abs(decimal) - degrees) * 60 - minutes) * 60;
  const formattedDegrees = String(degrees).padStart(2, '0');
  const formattedMinutes = minutes.toFixed(0).padStart(2, '0');
  const formattedSeconds = seconds.toFixed(0).padStart(2, '0');
  const direction =
    type === 'lat' ? (decimal >= 0 ? 'N' : 'S') : decimal >= 0 ? 'E' : 'W';
  return `${formattedDegrees}°${formattedMinutes}'${formattedSeconds}"${direction}`;
};

export const convertToDDMM = (decimal: number, type: 'lat' | 'lon') => {
  const degrees = Math.floor(Math.abs(decimal));
  const minutes = (Math.abs(decimal) - degrees) * 60;
  const formattedDegrees = String(degrees).padStart(2, '0');

  // Fix: Handle the case where toFixed might not include decimal part
  const minutesFixed = minutes.toFixed(4);
  const [wholePart, decimalPart = '0000'] = minutesFixed.split('.');

  const formattedMinutes =
    wholePart.padStart(2, '0') + '.' + decimalPart.padStart(4, '0');

  const direction =
    type === 'lat' ? (decimal >= 0 ? 'N' : 'S') : decimal >= 0 ? 'E' : 'W';
  return `${formattedDegrees}°${formattedMinutes}'${direction}`;
};

export const convertToDegrees = (decimal: number, type: 'lat' | 'lon') => {
  const degrees = Math.abs(decimal);
  const formattedDegrees = degrees.toFixed(6).padStart(6, '0');
  const direction =
    type === 'lat' ? (decimal >= 0 ? 'N' : 'S') : decimal >= 0 ? 'E' : 'W';
  return `${formattedDegrees}°${direction}`;
};

/**
 * Parse coordinate strings back to decimal format
 * Supports DMS (DD°MM'SS"N), DDMM (DD°MM.MMMM'N), and decimal degree (DD.dddddd°N) formats
 */
export const parseCoordinateToDecimal = (
  coordinateString: string,
): number | null => {
  if (!coordinateString) {
    return null;
  }

  const trimmed = coordinateString.trim();

  // Check if it's already a decimal number (must be ONLY digits, decimal point, and optional minus sign)
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    const asNumber = parseFloat(trimmed);
    if (!isNaN(asNumber) && isFinite(asNumber)) {
      return asNumber;
    }
  }

  // Extract direction (N, S, E, W)
  const directionMatch = trimmed.match(/[NSEW]$/i);
  if (!directionMatch) {
    return null;
  }

  const direction = directionMatch[0].toUpperCase();
  const coordinate = trimmed.slice(0, -1);

  let decimal: number;

  // DMS format: DD°MM'SS"
  const dmsMatch = coordinate.match(/^(\d+)°(\d+)[''′](\d+(?:\.\d+)?)[""″]?$/);
  if (dmsMatch) {
    const degrees = parseInt(dmsMatch[1], 10);
    const minutes = parseInt(dmsMatch[2], 10);
    const seconds = parseFloat(dmsMatch[3]);
    decimal = degrees + minutes / 60 + seconds / 3600;
  }
  // DDMM format: DD°MM.MMMM'
  else {
    const ddmmMatch = coordinate.match(/^(\d+)°(\d+(?:\.\d+)?)[''′″]?$/);
    if (ddmmMatch) {
      const degrees = parseInt(ddmmMatch[1], 10);
      const minutes = parseFloat(ddmmMatch[2]);
      decimal = degrees + minutes / 60;
    }
    // Decimal degrees format: DD.dddddd°
    else {
      const degreesMatch = coordinate.match(/^(\d+(?:\.\d+)?)°?$/);
      if (degreesMatch) {
        decimal = parseFloat(degreesMatch[1]);
      } else {
        return null;
      }
    }
  }

  // Apply direction
  if (direction === 'S' || direction === 'W') {
    decimal = -decimal;
  }

  return decimal;
};
